import { commercialReference } from '../quoteReference';
import React, { useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '../../../shared/components/Alert/Alert';
import Button from '../../../shared/components/Button/Button';
import { roundingModes } from '../quoteUiOptions';
import { quoteCommercialStatuses, quoteTypes } from '../quoteEditorModel';
import QuoteSettingsPanel from './QuoteSettingsPanel';
import QuoteForceValueDialog from './QuoteForceValueDialog';
import QuoteInformationPanel from './QuoteInformationPanel';
import QuoteContractTermsPanel from './QuoteContractTermsPanel';
import QuoteCostingWorkspace from './QuoteCostingWorkspace';
import { useQuoteCompositionPreview } from '../composition/previewContext';
import QuoteExpensesPanel from './QuoteExpensesPanel';
import QuoteSalesSheet from './QuoteSalesSheet';
import QuoteOptionsPanel from './QuoteOptionsPanel';
import QuoteMinute from './QuoteMinute';
import QuoteDocumentsPanel from './QuoteDocumentsPanel';
import { CompositionOptions, CompositionScopeFinance } from '../composition/CompositionScopePanels';
import { quoteDocumentReadiness } from '../quoteDocumentReadiness';
import CompositionPromotionNotice from '../composition/CompositionPromotionNotice';
import QuoteHelp from './QuoteHelp';
import QuoteContextMenu from './QuoteContextMenu';
import {
  QUOTE_VERSION_STATUS,
  QUOTE_VERSION_STATUS_LABELS,
} from '../quoteVersionStatus';

const editorSteps = [
  ['information', '1. Informations'],
  ['contract', '2. Conditions de la prestation'],
  ['parameters', '3. Paramètres'],
  ['costing', '4. Chiffrage'],
  ['expenses', '5. Frais'],
  ['options', '6. Options'],
  ['sales', '7. Feuille de vente'],
  ['minute', '8. Minute'],
  ['documents', '9. Documents'],
];

const QuoteEditor = ({
  editor,
  composition: requestedComposition = null,
  developmentPreview = false,
  compositionAccess = null,
  onEnableComposition,
  projects,
  affairs,
  calculated,
  calculationStatus,
  calculationError,
  visibleLines,
  saving,
  formatMoney: euroFormatMoney,
  getProjectName,
  onUpdateEditor,
  onApplyCustomerSnapshot,
  onApplyPartnerTerms,
  onUpdateParameters,
  onUpdateExpenses,
  onUpdateLine,
  onApplyCatalogMaterial,
  onAddVisibleLine,
  onAddTextLine,
  onAddSection,
  onUpdateSection,
  onRemoveSection,
  onImportSections,
  planningQuoteDraft,
  planningQuoteDraftLoading,
  onImportPlanning,
  onRemoveLine,
  onDuplicateLine,
  onMoveLine,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onClearOptions,
  onAddOptionLine,
  onUpdateOptionLine,
  onRemoveOptionLine,
  onForceLineValue,
  onResetLineValue,
  onVersionCommand,
  onPreviewDocument,
  onGenerateOfficialDocument,
  onArchiveDocumentGed,
  onDownloadDocument,
  onPreviewPrintTemplate,
  onPreviewInternalTemplate,
  onPrintInternalTemplate,
  onPrintPdf,
  onSave,
  onClose,
}) => {
  const previewConfiguration = useQuoteCompositionPreview();
  const isolated = Boolean(developmentPreview && previewConfiguration);
  const activeComposition = editor.contentSchemaVersion === 2 && (requestedComposition?.operational || isolated) ? requestedComposition : null;
  const compositionPending = editor.contentSchemaVersion === 2 && !activeComposition;
  const compositionBusy = ['loading', 'calculating'].includes(activeComposition?.status);
  saving = saving || compositionBusy;
  const composition = activeComposition && saving ? { ...activeComposition, canEdit: false } : activeComposition;
  if (compositionBusy || compositionPending || (composition && saving)) editor = { ...editor, permissions: { ...editor.permissions, canEdit: false } };
  const currencyCode = editor.currencyCode ?? 'EUR';
  const currencyUnsupported = currencyCode !== 'EUR';
  const formatMoney = currencyUnsupported
    ? (value) => `${Number(value ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode}`
    : euroFormatMoney;
  if (currencyUnsupported) editor = { ...editor, permissions: { ...editor.permissions,
    canEdit: false, canForcePrice: false, canForceCoefficient: false, canValidate: false, canIssue: false, canClone: false } };
  const [forceDialog, setForceDialog] = useState(null);
  const [activeStep, setActiveStep] = useState('information');
  const addSelectedLine = (quoteSectionId, overrides = {}) => {
    onAddVisibleLine?.({ quoteSectionId, lineType: 'service', ...overrides });
  };
  const engineTotal = composition?.entry.totals?.totalHT ?? calculated.totals?.totalSellingPriceHT
    ?? calculated.totals?.totalHT
    ?? 0;
  const calculationIsPending = ['stale', 'calculating'].includes(calculationStatus);
  const calculationIsValid = calculationStatus === 'validated';
  const documentReadiness = quoteDocumentReadiness({ editor, calculated, calculationStatus, pendingComposition: composition?.hasPendingChanges });
  const costingLines = useMemo(() => {
    const calculatedById = new Map((calculated.internalLines || []).map((line) => [String(line.id), line]));
    return (editor.sections || []).flatMap((section) => (section.lines || []).map((line) => ({
      ...line,
      ...(calculatedById.get(String(line.id)) || {}),
      // Le type/texte et la section sont des données éditoriales, non calculées par quote_engine.
      lineType: line.lineType,
      description: line.description,
      quoteSectionId: line.quoteSectionId || section.id,
      isClientVisible: line.isClientVisible,
    })));
  }, [editor.sections, calculated.internalLines]);


  return (
  <>
    <section className="quote-topbar">
      {currencyUnsupported && <p role="alert">Devise {currencyCode} historique : lecture seule en V1. Modification, recalcul, duplication, émission et génération documentaire indisponibles. Montants conservés sans conversion.</p>}
      <div className="quote-topbar__navigation">
        <Button className="quote-topbar__back" size="small" variant="secondary" onClick={onClose} disabled={saving} startIcon={<ArrowBackIcon fontSize="small" />} aria-label="Retour à la liste des devis">
          Retour aux devis
        </Button>
      </div>
      <div className="quote-topbar__primary">
        <div className="quote-topbar__identity">
          <span>Devis</span>
          <strong>{commercialReference(editor.quoteNumber, editor.versionNumber) || 'Nouveau devis'}</strong>
        </div>
        <label className="quote-topbar__title">
          <span>Titre</span>
          <input disabled={!editor.permissions?.canEdit} value={editor.title} onChange={(event) => onUpdateEditor('title', event.target.value)} />
        </label>
        <div className="quote-topbar__badges" aria-label="État du devis">
          <div>
            <span>Version</span>
            <strong>V{editor.versionNumber}</strong>
          </div>
          <strong className={`quote-version-status quote-version-status--${editor.versionStatus}`}>
            {QUOTE_VERSION_STATUS_LABELS[editor.versionStatus] || editor.versionStatus}
          </strong>
          <strong className={`quote-commercial-status quote-commercial-status--${editor.commercialStatus}`}>
            {quoteCommercialStatuses.find(([key]) => key === editor.commercialStatus)?.[1] || editor.commercialStatus}
          </strong>
        </div>
        <label className="quote-topbar__type">
          <span>Type</span>
          <select disabled={!editor.permissions?.canEdit} value={editor.quoteType || 'commercial'} onChange={(event) => onUpdateEditor('quoteType', event.target.value)}>
            {quoteTypes.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
      </div>

      <div className="quote-topbar__secondary">
        <label className="quote-topbar__affair">
          <span>Affaire</span>
          <select disabled={!editor.permissions?.canEdit} value={editor.affairId || ''} onChange={(event) => onUpdateEditor('affairId', event.target.value)}>
            <option value="">Sans affaire</option>
            {affairs.map((affair) => <option key={affair.id} value={affair.id}>{affair.code} - {affair.name}</option>)}
          </select>
        </label>

        <label className="quote-topbar__project">
          <span>Projet</span>
          <select disabled={!editor.permissions?.canEdit} value={editor.projectId} onChange={(event) => onUpdateEditor('projectId', event.target.value)}>
            <option value="">Sans projet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.code ? `${project.code} - ${getProjectName(project)}` : getProjectName(project)}
              </option>
            ))}
          </select>
        </label>

        <div className="quote-topbar__actions" aria-label="Actions du devis">
          <div className="quote-topbar__document-actions">
            <button className="basic-button" type="button" onClick={onPreviewPrintTemplate} disabled={saving || (!composition && !documentReadiness.ready)} title={documentReadiness.message || undefined}>
              Aperçu
            </button>
            <button className="basic-button" type="button" onClick={onPrintPdf} disabled={isolated || saving || !documentReadiness.ready} title={documentReadiness.message || undefined}>
              Imprimer / PDF
            </button>
          </div>
          {!documentReadiness.ready && <span role="status">{documentReadiness.message}</span>}
          <div className="quote-topbar__workflow-actions">
            {editor.permissions?.canValidate && <button className="basic-button" type="button" onClick={() => onVersionCommand('validate')} disabled={saving}>Valider</button>}
            {editor.permissions?.canReopen && <button className="basic-button" type="button" onClick={() => onVersionCommand('reopen')} disabled={saving}>Repasser en brouillon</button>}
            {editor.permissions?.canIssue && <button className="basic-button" type="button" onClick={() => onVersionCommand('issue')} disabled={saving}>Émettre</button>}
            {editor.permissions?.canSend && <button className="basic-button" type="button" onClick={() => onVersionCommand('send')} disabled={saving}>Envoyer au client</button>}
            {editor.permissions?.canAccept && <button className="basic-button" type="button" onClick={() => onVersionCommand('accept')} disabled={saving}>Accepter</button>}
            {editor.permissions?.canRefuse && <button className="basic-button" type="button" onClick={() => onVersionCommand('refuse')} disabled={saving}>Refuser</button>}
            {editor.permissions?.canCancel && <button className="basic-button" type="button" onClick={() => onVersionCommand('cancel')} disabled={saving}>{['draft', 'validated'].includes(editor.versionStatus) ? 'Abandonner' : 'Annuler'}</button>}
            {editor.permissions?.canClone && editor.versionStatus !== QUOTE_VERSION_STATUS.DRAFT && (
              <button className="basic-button" type="button" onClick={() => onVersionCommand('clone')} disabled={saving}>Modifier (nouvelle version)</button>
            )}
          </div>
          <button className="basic-button active quote-topbar__save" type="button" onClick={onSave} disabled={isolated || saving || calculationStatus === 'error' || !editor.permissions?.canEdit}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </section>

    {composition ? <Alert className={`quote-composition-status ${composition.reliable && !composition.error ? 'quote-composition-status--normal' : ''}`} severity={composition.error ? 'error' : composition.reliable ? 'success' : 'warning'}>
      <span className="quote-summary-total"><span>{composition.entry.scope.kind === 'main' ? 'Total devis HT' : 'Total option HT'}</span><strong>{composition.reliable ? formatMoney(engineTotal) : 'Indisponible'}</strong></span>
      <span className="quote-summary-state">{({ complete: 'Calcul vérifié', loading: 'Chargement…', calculating: 'Calcul en cours…', incomplete: 'Ensemble incomplet : ajouter des prestations. Aucun total certifié.', stale: 'Résultat périmé : recalcul explicite requis.', conflict: 'Conflit de révision : rechargez le brouillon avant de modifier.', 'engine-error': 'Erreur moteur : aucun montant fiable disponible.', invalid: 'Structure invalide : résultat non exploitable.' })[composition.status]}</span>
      <span className="quote-summary-save">{saving || compositionBusy ? 'Enregistrement…' : composition.hasPendingChanges ? 'Modifications à appliquer' : composition.operational ? 'Composition enregistrée' : 'Prévisualisation locale'}</span>
      <span className="quote-summary-version" aria-label="Ensembles composés activés">V2 <QuoteHelp title="Composition V2">{`Ensembles composés activés · Révision ${composition.model.revision}. Les montants proviennent du dernier calcul vérifié de ce périmètre.`}</QuoteHelp></span>
      <QuoteContextMenu label="Calcul" ariaLabel="Actions de calcul" items={[
        { label: 'Recalculer', disabled: !composition.canEdit, help: composition.canEdit ? 'Relancer le calcul du périmètre sélectionné.' : 'Le recalcul nécessite un brouillon modifiable.', onClick: () => composition.run({ type: 'calculate', scope: composition.entry.scope }) },
        { label: 'Recharger la version', disabled: ['loading', 'calculating'].includes(composition.status), help: 'Relire la version enregistrée. Les protections des saisies en cours restent actives.', onClick: composition.reload },
      ]} />
      {composition.error && <span className="quote-summary-error">{composition.error}</span>}
    </Alert> : <Alert severity={
      calculationStatus === 'error' ? 'error' : calculationIsPending ? 'warning' : calculationIsValid ? 'success' : 'info'
    }>
      {calculationStatus === 'error' ? (
        <span>
          <strong>Calcul impossible.</strong> {calculationError?.message}
          {calculationError?.path ? ` - ${calculationError.path}` : ''}
        </span>
      ) : (
        <span>
          <strong>Total devis HT : {formatMoney(engineTotal)}</strong>
          {calculationStatus === 'recorded' ? <span> - montants enregistrés sans recalcul</span> : null}
          {calculationIsValid ? <span className="quote-engine-version"> - calcul à jour</span> : ''}
          {calculationStatus === 'calculating' ? ' - calcul en cours…' : ''}
          {calculationStatus === 'stale' ? ' - recalcul programmé après modification' : ''}
        </span>
      )}
    </Alert>}

    {(calculated.warnings?.messages || []).map((warning) => (
      <Alert key={warning.code} severity={warning.severity || 'warning'}>
        {warning.message}
      </Alert>
    ))}

    <nav className="quote-editor-steps" aria-label="Étapes de la version">
      {editorSteps.filter(([key]) => {
        if (['parameters', 'expenses', 'options', 'sales'].includes(key)) return editor.permissions?.canViewCosts;
        if (key === 'minute') return editor.permissions?.canViewMinute;
        return true;
      }).map(([key, label]) => (
        <button
          key={key}
          className={activeStep === key ? 'active' : ''}
          aria-current={activeStep === key ? 'step' : undefined}
          aria-label={label}
          type="button"
          onClick={() => setActiveStep(key)}
        >
          {key === 'contract' ? '2. Conditions' : label}
        </button>
      ))}
    </nav>

    {activeStep === 'information' && (
      <QuoteInformationPanel
        editor={editor}
        roundingModes={roundingModes}
        onUpdate={onUpdateEditor}
        localOnly={isolated}
        onApplySnapshot={onApplyCustomerSnapshot}
        onApplyPartnerTerms={onApplyPartnerTerms}
      />
    )}

    {activeStep === 'parameters' && (
      <QuoteSettingsPanel
        localOnly={isolated}
        editor={editor}
        roundingModes={roundingModes}
        onUpdate={onUpdateEditor}
        onUpdateParameters={onUpdateParameters}
      />
    )}

    {activeStep === 'contract' && (
      <QuoteContractTermsPanel
        editor={editor}
        planningQuoteDraft={planningQuoteDraft}
        onUpdate={onUpdateEditor}
      />
    )}

    {activeStep === 'costing' && compositionAccess && <CompositionPromotionNotice editor={editor} access={compositionAccess} saving={saving} onEnable={onEnableComposition} />}
    {activeStep === 'costing' && compositionPending ? <Alert severity="info">Chargement du chiffrage composé…</Alert> : activeStep === 'costing' && (
      <QuoteCostingWorkspace
        editor={editor}
        composition={composition}
        localOnly={isolated}
        lines={costingLines}
        calculated={calculated}
        formatMoney={formatMoney}
        onAddLine={addSelectedLine}
        onUpdateLine={onUpdateLine}
        onRemoveLine={onRemoveLine}
        onDuplicateLine={onDuplicateLine}
        onMoveLine={onMoveLine}
        onForceValue={(line, kind) => setForceDialog({ line, kind })}
        onResetValue={onResetLineValue}
        onApplyCatalogMaterial={onApplyCatalogMaterial}
        onAddTextLine={onAddTextLine}
        onAddSection={onAddSection}
        onUpdateSection={onUpdateSection}
        onRemoveSection={onRemoveSection}
        onImportSections={onImportSections}
        planningQuoteDraft={planningQuoteDraft}
        planningQuoteDraftLoading={planningQuoteDraftLoading}
        onImportPlanning={onImportPlanning}
        onSave={onSave}
        saving={saving}
        saveDisabled={isolated || saving || calculationStatus === 'error' || !editor.permissions?.canEdit}
      />
    )}

    {activeStep === 'expenses' && composition?.operational ? <CompositionScopeFinance key={JSON.stringify(composition.entry.scope)} composition={composition} editor={editor} formatMoney={formatMoney} /> : activeStep === 'expenses' && composition ? <section className="quote-panel"><h2>Frais et charges</h2><p>Les frais de chaque périmètre restent ceux du résultat Python vérifié. Leur édition opérationnelle est désactivée dans la prévisualisation B4.1.</p></section> : activeStep === 'expenses' && (
      <QuoteExpensesPanel
        editor={editor}
        lines={visibleLines}
        totals={calculated.totals}
        salesSheet={calculated.salesSheet}
        formatMoney={formatMoney}
        onUpdateExpenses={onUpdateExpenses}
      />
    )}


    {activeStep === 'options' && composition?.operational ? <CompositionOptions composition={composition} /> : activeStep === 'options' && composition ? <section className="quote-panel"><h2>Options</h2><p>Chaque périmètre reste autonome, sans héritage des frais ni total cumulé.</p><label>Périmètre<select aria-label="Périmètre" value={composition.scopeIndex} onChange={e => composition.setScopeIndex(Number(e.target.value))}>{composition.model.scopes.map((s, i) => <option key={i} value={i}>{i === 0 ? 'Devis principal' : `Option · ${s.label}`}</option>)}</select></label><button className="basic-button" onClick={() => setActiveStep('costing')}>Chiffrer ce périmètre</button></section> : activeStep === 'options' && (
      <QuoteOptionsPanel
        editor={editor}
        calculated={calculated}
        formatMoney={formatMoney}
        onAddOption={onAddOption}
        onUpdateOption={onUpdateOption}
        onRemoveOption={onRemoveOption}
        onClearOptions={onClearOptions}
        onAddOptionLine={onAddOptionLine}
        onUpdateOptionLine={onUpdateOptionLine}
        onRemoveOptionLine={onRemoveOptionLine}
      />
    )}

    {composition?.operational && ['sales', 'minute'].includes(activeStep) ? activeStep === 'sales'
      ? <QuoteSalesSheet editor={editor} composition={composition} onPreviewInternalTemplate={onPreviewInternalTemplate} />
      : <QuoteMinute editor={editor} composition={composition} onPreviewInternalTemplate={onPreviewInternalTemplate} />
      : composition && !composition.operational && ['sales', 'minute', 'documents'].includes(activeStep) ? <section className="quote-panel"><h2>{editorSteps.find(([key]) => key === activeStep)?.[1]}</h2><p>Cette prévisualisation B4.1 ne raccorde pas les générateurs opérationnels. Utilisez l’aperçu client autorisé en haut de l’éditeur.</p></section> : activeStep === 'sales' && editor.permissions?.canViewCosts && (
      <QuoteSalesSheet editor={editor} calculated={calculated} calculationStatus={calculationStatus} onPreviewInternalTemplate={onPreviewInternalTemplate} />
    )}

    {activeStep === 'minute' && !composition && (
      <QuoteMinute editor={editor} calculated={calculated} calculationStatus={calculationStatus} onPreviewInternalTemplate={onPreviewInternalTemplate} />
    )}

    {activeStep === 'documents' && (!composition || composition.operational) && (
      <QuoteDocumentsPanel
        editor={composition?.operational ? { ...editor, pendingComposition: composition.hasPendingChanges } : editor}
        calculated={calculated}
        calculationStatus={calculationStatus}
        saving={saving}
        onPreview={onPreviewDocument}
        onPreviewPrintTemplate={onPreviewPrintTemplate}
        onPreviewInternalTemplate={onPreviewInternalTemplate}
        onPrintInternalTemplate={onPrintInternalTemplate}
        onGenerateOfficial={onGenerateOfficialDocument}
        onArchiveGed={onArchiveDocumentGed}
        onDownload={onDownloadDocument}
      />
    )}
    <QuoteForceValueDialog
      open={Boolean(forceDialog)}
      kind={forceDialog?.kind}
      line={forceDialog?.line}
      onClose={() => setForceDialog(null)}
      onConfirm={async (payload) => {
        await onForceLineValue(forceDialog.line, forceDialog.kind, payload);
        setForceDialog(null);
      }}
    />
  </>
  );
};

export default QuoteEditor;
